import axios from "axios";
import installSendGuard from "./sendGuard";

const api = axios.create({
	baseURL: process.env.REACT_APP_BACKEND_URL || window.location.origin,
	withCredentials: true,
});

// ✅ FLAG GLOBAL PARA PREVENIR MÚLTIPLES REFRESH SIMULTÁNEOS
let isRefreshing = false;
let failedQueue = [];
let lastRefreshAttempt = 0;
const MIN_REFRESH_INTERVAL = 5000; // ✅ Mínimo 5 segundos entre refresh attempts

// ✅ CANCEL TOKEN SOURCE PARA CANCELAR PETICIONES AL HACER LOGOUT
let cancelTokenSource = axios.CancelToken.source();
let isLoggingOut = false; // ✅ Flag para indicar que se está haciendo logout

const processQueue = (error, token = null) => {
	failedQueue.forEach(prom => {
		if (error) {
			prom.reject(error);
		} else {
			prom.resolve(token);
		}
	});
	failedQueue = [];
};

// ✅ INTERCEPTOR MEJORADO PARA MANEJO AUTOMÁTICO DE TOKENS
api.interceptors.request.use(
  (config) => {
    // ✅ CRÍTICO: Si se está haciendo logout, cancelar TODAS las peticiones (incluyendo logout si ya se procesó)
    if (isLoggingOut) {
      // ✅ Cancelar la petición inmediatamente
      config.cancelToken = cancelTokenSource.token;
      // ✅ NO adjuntar token si se está haciendo logout
      return config;
    }
    
    // Configuración especial para endpoints de actualización (timeout extendido)
    if (config.url.includes('/system/perform-') || config.url.includes('/system/check-updates')) {
      config.timeout = 300000; // 5 minutos para actualizaciones
    }
    
    // Solo verificar token si NO es una ruta de autenticación
    if (!config.url.includes('/auth/') && !config.url.includes('/login')) {
        // ✅ VERIFICAR TOKEN VÁLIDO ANTES DE ADJUNTAR
        const token = localStorage.getItem("token");
        if (token && token !== 'null' && token !== 'undefined') {
          config.headers = config.headers || {};
          config.headers["Authorization"] = `Bearer ${token}`;
        } else {
          // ✅ SOLO LOGGEAR EN DESARROLLO PARA EVITAR SPAM EN PRODUCCIÓN
          // ✅ NO LOGGEAR PARA RUTAS PÚBLICAS O QUE NO REQUIEREN AUTENTICACIÓN
          const publicRoutes = [
            '/auth/', 
            '/login', 
            '/settings/viewregister',
            // ✅ Settings públicos (no requieren autenticación)
            '/settings/websiteTitle',
            '/settings/websiteDescription',
            '/settings/websiteKeyword',
            '/settings/companyName',
            '/public/negocio'
          ];
          const isPublicRoute = publicRoutes.some(route => config.url.includes(route));
          
          if (window.location.hostname === 'localhost' && !isPublicRoute) {
            console.log(`❌ API REQUEST: ${config.method?.toUpperCase()} ${config.url} - SIN TOKEN VÁLIDO`);
          }
          // ✅ NO CANCELAR LA PETICIÓN, DEJAR QUE EL BACKEND MANEJE EL ERROR
        }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// ✅ INTERCEPTOR DE RESPUESTA CON MANEJO DE REFRESH TOKEN CENTRALIZADO
api.interceptors.response.use(
  (response) => {
    const method = String(response?.config?.method || "").toLowerCase();
    const sentTo = String(response?.config?.url || "").match(/^\/?messages\/(\d+)$/);
    if (method === "post" && sentTo && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("watoolx:message-sent", { detail: { ticketId: sentTo[1] } }));
    }
    const businessPath = String(response?.config?.url || "").match(/^\/?negocio\/([^/?]+)/);
    if (businessPath && ["post", "put", "patch", "delete"].includes(method) && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("negocio:changed", { detail: { entity: businessPath[1] } }));
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error?.response?.data?.error === "ERR_COMPANY_EXPIRED") {
      if (typeof window !== "undefined" && window.location.pathname !== "/financeiro") {
        window.location.assign("/financeiro");
      }
      return Promise.reject(error);
    }
    
    // ✅ MANEJAR ERROR 429 (Too Many Requests) - NO INTENTAR REFRESH
    if (error?.response?.status === 429) {
      const isRefreshTokenEndpoint = originalRequest.url?.includes('/auth/refresh_token');
      
      if (isRefreshTokenEndpoint) {
        // ✅ Si el refresh_token devuelve 429, esperar antes de reintentar
        console.warn('⚠️ Error 429 en refresh_token - Demasiadas solicitudes. Esperando antes de reintentar...');
        
        // ✅ Limpiar flag de refresh para permitir reintento después del delay
        isRefreshing = false;
        lastRefreshAttempt = Date.now();
        
        // ✅ Esperar el intervalo mínimo antes de permitir otro intento
        // Las peticiones posteriores esperarán el intervalo mínimo antes de intentar refresh
        return Promise.reject(error);
      } else {
        // ✅ Para otros endpoints con 429, solo rechazar sin intentar refresh
        console.warn(`⚠️ Error 429 en ${originalRequest.url} - Demasiadas solicitudes`);
        return Promise.reject(error);
      }
    }
    
    // ✅ FIX: Manejar errores 401 y 403 con refresh token
    const isAuthError = error?.response?.status === 403 || error?.response?.status === 401;
    
    // ✅ CRÍTICO: Si se está haciendo logout, no intentar refresh token
    // Esto evita múltiples warnings de "Invalid token" cuando el usuario cierra sesión
    if (isLoggingOut) {
      return Promise.reject(error);
    }
    
    // ✅ Si es error de autenticación y no es el endpoint de refresh_token
    if (isAuthError && !originalRequest._retry && !originalRequest.url?.includes('/auth/refresh_token')) {
      originalRequest._retry = true;

      // ✅ Verificar si ha pasado suficiente tiempo desde el último intento de refresh
      const timeSinceLastRefresh = Date.now() - lastRefreshAttempt;
      if (timeSinceLastRefresh < MIN_REFRESH_INTERVAL) {
        const waitTime = Math.ceil((MIN_REFRESH_INTERVAL - timeSinceLastRefresh) / 1000);
        console.warn(`⚠️ Esperando ${waitTime}s antes de intentar refresh token...`);
        // ✅ Esperar el tiempo restante y luego reintentar la petición original
        await new Promise(resolve => setTimeout(resolve, MIN_REFRESH_INTERVAL - timeSinceLastRefresh));
        // ✅ Después de esperar, resetear el flag para permitir reintento
        originalRequest._retry = false;
        // ✅ Reintentar la petición original
        return api(originalRequest);
      }

      // ✅ Si ya hay un refresh en proceso, encolar esta petición
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) {
        // No hay refresh token, limpiar sesión
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("companyId");
        localStorage.removeItem("userId");
        api.defaults.headers.Authorization = undefined;
        return Promise.reject(error);
      }

      isRefreshing = true;
      lastRefreshAttempt = Date.now();

      try {
        const { data } = await api.post("/auth/refresh_token", { refreshToken });
        
        if (data && data.token) {
          localStorage.setItem("token", data.token);
          if (data.refreshToken) {
            localStorage.setItem("refreshToken", data.refreshToken);
          }
          if (data.user?.companyId != null && data.user.companyId !== "") {
            localStorage.setItem("companyId", String(data.user.companyId));
          }
          if (data.user?.id != null && data.user.id !== "") {
            localStorage.setItem("userId", String(data.user.id));
          }
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          
          // ✅ Procesar cola de peticiones fallidas
          processQueue(null, data.token);
          
          // ✅ Reintentar petición original
          originalRequest.headers.Authorization = `Bearer ${data.token}`;
          isRefreshing = false;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // ✅ Si el refresh devuelve 429, no limpiar sesión, solo rechazar
        if (refreshError?.response?.status === 429) {
          console.warn('⚠️ Error 429 en refresh_token - Demasiadas solicitudes. No se limpiará la sesión.');
          processQueue(refreshError, null);
          isRefreshing = false;
          return Promise.reject(refreshError);
        }
        
        // ✅ Si falla el refresh por otro motivo, limpiar todo y procesar cola con error
        processQueue(refreshError, null);
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("companyId");
        localStorage.removeItem("userId");
        api.defaults.headers.Authorization = undefined;
        isRefreshing = false;
        return Promise.reject(refreshError);
      }
    }
    
    // ✅ Para otros errores, solo loggear en desarrollo
    if (window.location.hostname === 'localhost' && error.response?.status !== 401 && error.response?.status !== 403 && error.response?.status !== 429) {
      const status = error.response?.status;
      const hint = status === undefined
        ? " (Sin respuesta: verificar que el backend esté corriendo, CORS, o REACT_APP_BACKEND_URL)"
        : "";
      console.log(`❌ API ERROR: ${error.config?.method?.toUpperCase()} ${error.config?.url} - Status: ${status ?? "N/A"}${hint}`);
    }
    
    return Promise.reject(error);
  }
);

// ✅ FUNCIÓN PARA CANCELAR TODAS LAS PETICIONES PENDIENTES (USAR EN LOGOUT)
export const cancelAllPendingRequests = () => {
  isLoggingOut = true; // ✅ Marcar que se está haciendo logout
  cancelTokenSource.cancel('Usuario cerró sesión');
  // ✅ Crear nueva fuente de cancelación para futuras peticiones
  cancelTokenSource = axios.CancelToken.source();
};

// ✅ FUNCIÓN PARA RESETEAR EL FLAG DE LOGOUT (USAR DESPUÉS DE LOGIN)
export const resetLogoutFlag = () => {
  isLoggingOut = false;
};

export const openApi = axios.create({
	baseURL: process.env.REACT_APP_BACKEND_URL || window.location.origin
});

// ✅ HELPER: Construir URL completa para imágenes de perfil
export const getProfilePicUrl = (profilePicUrl) => {
	if (!profilePicUrl) return null;
	
	// ✅ Si ya es una URL completa (http/https), retornarla tal cual
	if (profilePicUrl.startsWith('http://') || profilePicUrl.startsWith('https://')) {
		return profilePicUrl;
	}
	
	// ✅ Si es una ruta relativa que empieza con /public/, construir URL completa del backend
	if (profilePicUrl.startsWith('/public/')) {
		const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
		return `${backendUrl}${profilePicUrl}`;
	}
	
	// ✅ Si es una ruta relativa sin /public/, asumir que es del backend
	if (profilePicUrl.startsWith('/')) {
		const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
		return `${backendUrl}${profilePicUrl}`;
	}
	
	// ✅ Fallback: retornar tal cual (puede ser una URL externa sin protocolo)
	return profilePicUrl;
};

installSendGuard(api);

export default api;
